import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SimpleGrid,
  Paper,
  Title,
  Text,
  Center,
  Card,
  Image,
  Group,
  Badge,
  Box,
  Stack,
  Tooltip,
  Skeleton,
  MultiSelect,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import moment from "moment";
import {
  IconArmchair,
  IconParking,
  IconDoorEnter,
  IconDoorExit,
  IconBeach,
  IconEye,
  IconUsers,
} from "@tabler/icons";
import ModuleAppleReleases from "./releases/ModuleAppleReleases";
import ModuleGoogleReleases from "./releases/ModuleGoogleRelease";
import ModuleCurrentBrowser from "./releases/ModuleCurrentBrowser";
import ModuleCurrentDevice from "./releases/ModuleCurrentDevice";
import IconBadge from "../components/IconBadge";
import { namesLikelyMatch } from "../components/Extensions";
import NotificationPermissionBanner from "../components/NotificationPermissionBanner";
import * as apiService from "../api-service";

import { getPosts, getAllEvents, getPostViews } from "../services/Firestore";
import { isPostForViewer } from "../services/UserRoles";

// A booking "covers" a given day if that day falls anywhere within its range, start and end day
// both included - mirrors the exact same check the Booker page itself uses. Day-granularity +
// inclusive-both-ends ("[]") rather than a raw instant isBetween, since an instant comparison is
// timezone-sensitive right at the boundary (see the matching comment on isOnDay in booker.js) -
// this was silently showing one extra trailing day of leave versus what eDays itself reports.
const isOnDay = (d, day) =>
  moment(day).isBetween(moment(d.data().date_start.toDate()), moment(d.data().date_end.toDate()), "day", "[]");

function Dashboard({ companyId, companyData, currentUser, userClaims, userToken }) {
  const navigate = useNavigate();
  // theme.primaryColor tracks the company's own accent automatically (src/App.js sets
  // primaryColor: "accent" whenever a company has one configured, falling back to Mantine's own
  // default otherwise) - reading it here instead of companyData.accent directly means the post
  // title always matches whatever colour the rest of the app's primary-coloured elements
  // (buttons, tabs, etc.) are using, including picking the correct shade for dark mode.
  const theme = useMantineTheme();

  const isAdminOrSuper = userClaims?.["access"] === "admin" || userClaims?.["access"] === "super";

  useEffect(() => {
    document.title = "Dashboard - Cell Software Portal";
  }, []);

  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (companyId) {
      getPosts(companyId, currentUser?.uid)
        .then((snapshot) => {
          setPosts(snapshot.docs ?? []);
          setIsPostsLoading(false);
        })
        .catch(() => setIsPostsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Per-post view analytics (src/services/Firestore.js's getPostViews) - admin/super only, both
  // here (so a non-admin never even fires the request, which firestore.rules would reject anyway)
  // and enforced server-side. Fetched once per post as soon as it's admin/super and the post list
  // has loaded, keyed by postId so each card can look its own numbers up directly.
  const [postViewsById, setPostViewsById] = useState({});

  useEffect(() => {
    if (!companyId || !isAdminOrSuper || posts.length === 0) return;
    let cancelled = false;

    Promise.all(
      posts.map((p) =>
        getPostViews(companyId, p.id)
          .then((views) => [p.id, views])
          .catch(() => [p.id, []])
      )
    ).then((entries) => {
      if (!cancelled) {
        setPostViewsById(Object.fromEntries(entries));
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isAdminOrSuper, posts]);

  // "Who's in today" - a quick-glance strip of everyone with a desk booking or office
  // opening/closing duty for today, so anyone can see who's around without opening Booker.
  const [todayEvents, setTodayEvents] = useState([]);
  const [isTodayLoading, setIsTodayLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      // Only "today" is ever shown here, so there's no need to pull in the company's whole
      // booking history the way Booker (which lets someone browse arbitrary past/future weeks)
      // does - a booking that already ended before today can never appear in this strip anyway.
      getAllEvents(companyId, moment().startOf("day").toDate())
        .then((snapshot) => {
          setTodayEvents(snapshot.docs ?? []);
          setIsTodayLoading(false);
        })
        .catch(() => setIsTodayLoading(false));
    }
  }, [companyId]);

  // Away/Off entries come from the company's own e-days iCal feed (Edit Company > Booker) rather
  // than Firestore - a company without one configured (or one that's temporarily unreachable)
  // just shows no away events, the same way Booker itself already handles it.
  const [awayEvents, setAwayEvents] = useState([]);

  useEffect(() => {
    if (companyId && userToken) {
      apiService
        .getAwayEvents({ userIdToken: userToken, companyId })
        .then((data) => setAwayEvents(data?.events ?? []))
        .catch(() => setAwayEvents([]));
    }
  }, [companyId, userToken]);

  const rooms = companyData?.["rooms"] ?? [];
  const roomTitle = (id) => rooms.find((r) => r.id === id)?.title;
  const roomColor = (id) => rooms.find((r) => r.id === id)?.color;
  const parkingSpaces = companyData?.["parking_spaces"] ?? [];
  const parkingSpaceTitle = (id) => parkingSpaces.find((p) => p.id === id)?.title;
  const today = new Date();
  // A plain Date is a new object (and object identity) on every render, which would defeat the
  // useMemo calls below - this stays referentially the same for as long as it's still the same
  // calendar day, so those memos correctly skip recomputing on re-renders that aren't actually
  // caused by new data coming in.
  const todayKey = today.toDateString();
  // Who's In Today only makes sense Mon-Fri - isoWeekday() is 1 (Monday) through 7 (Sunday), so
  // anything past 5 is the weekend.
  const isWeekday = moment(today).isoWeekday() <= 5;

  // Shown relative to "today" so it reads naturally regardless of the exact date - a booking
  // currently in progress (started before today, ends today or later) counts as happening "Today"
  // rather than being skipped as already started.
  const formatDayLabel = (date) =>
    moment(date).calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "dddd",
      sameElse: "ddd, D MMM YYYY",
    });

  // Office duty colours match the green/red used for Opening/Closing on the Booker page itself.
  // Desk and Parking each get their own distinct colour too, rather than the default icon
  // colour, so all three rows are equally easy to tell apart at a glance - and rendered bolder
  // (stroke 2.5 instead of the default 2) so they read as clearly as the coloured Office icon
  // already did, rather than looking like a thinner, less important sibling.
  const DESK_COLOR = "#4263eb";
  const PARKING_COLOR = "#f76707";
  const OFFICE_OPEN_COLOR = "#2f9e44";
  const OFFICE_CLOSE_COLOR = "#e03131";
  // Yellow rather than green - green's already taken by Office Duty's "Opening" colour above,
  // and this matches the amber used for "Planned Leave" on the Booker page itself.
  const AWAY_COLOR = "#f59f00";
  const FALLBACK_COLOR = "var(--app-accent-color, #228be6)";

  const awayName = userClaims?.["away_name"] || currentUser?.displayName || "";

  // Re-filtering/sorting todayEvents and awayEvents (and rebuilding this array, complete with its
  // own icon elements) on every single render - including ones triggered by unrelated state like
  // typing in the tag filter below - was pure wasted work. This only recomputes when the
  // underlying data (or the day) actually changes.
  const myNextBookings = useMemo(() => {
    // Finds the soonest upcoming (or currently in-progress) booking of a given type assigned to
    // the signed-in user - "assigned to" meaning `created_by`, which is always the person the
    // booking is actually for, not necessarily whoever clicked Book (an admin can book on someone
    // else's behalf).
    const findNextBooking = (type, { includePastOffset } = {}) =>
      todayEvents
        .filter((d) => d.data().type === type)
        .filter((d) => d.data().created_by === currentUser?.uid)
        .filter((d) => type !== "desk" || (d.data()?.state !== 2 && d.data()?.state !== 0))
        .filter((d) => moment(d.data().date_end.toDate()).isSameOrAfter(includePastOffset ?? today))
        .sort((a, b) => a.data().date_start.toDate() - b.data().date_start.toDate())[0] ?? null;

    const nextDesk = findNextBooking("desk");
    const nextParking = findNextBooking("parking", { includePastOffset: new Date() });
    const nextOffice = findNextBooking("office");

    // The signed-in user's next Away/Off entry from the e-days feed - matched by name the same
    // way Booker matches an assigned employee against it (namesLikelyMatch, with the "Away/Off
    // Calendar Name" claim as an override for names the heuristic can't resolve on its own).
    const nextAway =
      awayEvents
        .filter((e) => namesLikelyMatch(e.name, awayName))
        .filter((e) => moment(e.end).isSameOrAfter(today))
        .sort((a, b) => new Date(a.start) - new Date(b.start))[0] ?? null;

    return [
      {
        key: "desk",
        label: "Desk",
        color: DESK_COLOR,
        icon: <IconArmchair size="1.2rem" stroke={2.5} color={DESK_COLOR} />,
        event: nextDesk,
        detail: nextDesk && roomTitle(nextDesk.data().room),
        when: nextDesk && formatDayLabel(nextDesk.data().date_start.toDate()),
      },
      {
        key: "parking",
        label: "Parking",
        color: PARKING_COLOR,
        icon: <IconParking size="1.2rem" stroke={2.5} color={PARKING_COLOR} />,
        event: nextParking,
        detail: nextParking && parkingSpaceTitle(nextParking.data().space),
        when:
          nextParking &&
          `${formatDayLabel(nextParking.data().date_start.toDate())}, ${moment(
            nextParking.data().date_start.toDate()
          ).format("HH:mm")} - ${moment(nextParking.data().date_end.toDate()).format("HH:mm")}`,
      },
      {
        key: "office",
        label: "Office Duty",
        color: nextOffice?.data().office_type === "close" ? OFFICE_CLOSE_COLOR : OFFICE_OPEN_COLOR,
        icon:
          nextOffice?.data().office_type === "close" ? (
            <IconDoorExit size="1.2rem" stroke={2.5} color={OFFICE_CLOSE_COLOR} />
          ) : (
            <IconDoorEnter size="1.2rem" stroke={2.5} color={OFFICE_OPEN_COLOR} />
          ),
        event: nextOffice,
        detail: nextOffice && (nextOffice.data().office_type === "open" ? "Opening the office" : "Closing the office"),
        when: nextOffice && formatDayLabel(nextOffice.data().date_start.toDate()),
      },
      {
        key: "away",
        label: "Next Off",
        color: AWAY_COLOR,
        icon: <IconBeach size="1.2rem" stroke={2.5} color={AWAY_COLOR} />,
        event: nextAway,
        detail: nextAway?.status,
        when: nextAway && formatDayLabel(new Date(nextAway.start)),
        emptyText: "Nothing scheduled",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayEvents, awayEvents, currentUser?.uid, awayName, todayKey, companyData]);

  const whosInToday = useMemo(() => {
    const byName = new Map();
    // Each person's pill takes the colour of the first booking found for them (room colour for a
    // desk booking, green/red for office duty) - so at a glance you can tell which room someone's
    // in, not just that they're in.
    // Tracks a "group" per person too (room title, or "Office Duty") - not just for colour - so
    // everyone can be sorted by group afterwards and people in the same room end up bundled
    // together instead of being interleaved alphabetically by name.
    const addPerson = (name, detail, color, group) => {
      if (!name) return;
      if (!byName.has(name)) byName.set(name, { details: [], color: color ?? FALLBACK_COLOR, group: group ?? "Other" });
      if (detail) byName.get(name).details.push(detail);
    };

    todayEvents
      .filter((d) => d.data().type === "desk")
      // state 0/2 are cancelled/rejected bookings - matches the same filter Booker uses.
      .filter((d) => d.data()?.state !== 2 && d.data()?.state !== 0)
      .filter((d) => isOnDay(d, today))
      // Locations can be marked (Edit Company > Booker) to not count towards this widget at all -
      // e.g. a hot-desk area that isn't meaningful to track here - so those desk bookings are
      // dropped before ever reaching addPerson, same as a cancelled/rejected booking would be.
      .filter((d) => !rooms.find((r) => r.id === d.data().room)?.excluded_from_whos_in_today)
      .forEach((d) =>
        addPerson(d.data().title, roomTitle(d.data().room), roomColor(d.data().room), roomTitle(d.data().room))
      );

    todayEvents
      .filter((d) => d.data().type === "office")
      .filter((d) => isOnDay(d, today))
      .forEach((d) =>
        addPerson(
          d.data().title,
          d.data().office_type === "open" ? "Opening the office" : "Closing the office",
          d.data().office_type === "open" ? OFFICE_OPEN_COLOR : OFFICE_CLOSE_COLOR,
          "Office Duty"
        )
      );

    return [...byName.entries()]
      .map(([name, { details, color, group }]) => ({ name, details, color, group }))
      .sort((a, b) => a.group.localeCompare(b.group) || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayEvents, todayKey, companyData]);

  // A post with a future publish_date/time is queued up (set on the Posts admin page) but
  // shouldn't actually show here until that moment has passed - posts with no publish_date at
  // all (or one in the past) are always visible. A draft is hidden here regardless of
  // publish_date - even one already in the past - since it's still being written and hasn't
  // actually been queued/published yet. Separately, a post restricted to specific roles only
  // shows for viewers holding at least one of them - everyone else simply never sees it, the same
  // as if it didn't exist for them.
  const visiblePosts = useMemo(() => {
    return posts.filter((post) => {
      const data = post.data();
      const isDraft = data.is_draft ?? false;
      const isPublished = !data.publish_date || moment(data.publish_date.toDate()).isSameOrBefore(moment());
      const isForViewer = isPostForViewer(data, userClaims);
      return !isDraft && isPublished && isForViewer;
    });
  }, [posts, userClaims]);

  // Only offers tags that actually appear on a post this viewer can see - so picking a filter
  // never leads to a dead-end "no results" purely because the only post with that tag was
  // restricted to other roles or still scheduled.
  const availableTags = useMemo(
    () => Array.from(new Set(visiblePosts.flatMap((post) => post.data().tags ?? []))).sort(),
    [visiblePosts]
  );

  // No tags selected shows everything; otherwise a post matching ANY selected tag is shown
  // (rather than requiring all of them), which suits browsing/discovery better than an exact-match
  // filter would.
  const filteredPosts = useMemo(
    () =>
      selectedTags.length === 0
        ? visiblePosts
        : visiblePosts.filter((post) => (post.data().tags ?? []).some((t) => selectedTags.includes(t))),
    [visiblePosts, selectedTags]
  );

  const firstName = currentUser?.displayName?.split(" ")[0];

  return (
    <Stack spacing="xl">
      <NotificationPermissionBanner userToken={userToken} companyId={companyId} />

      {firstName && <Title order={2}>Welcome back, {firstName}</Title>}

      {/* Current OS/browser release versions - kept pinned at the very top of the page. A single
          SimpleGrid (rather than two near-duplicate MediaQuery-gated blocks) so there's only one
          copy of this markup to keep in sync. 2 columns on mobile (a compact 2x2 grid) rather
          than stacking to a single column, so all four stay visible without much scrolling. */}
      <SimpleGrid
        cols={4}
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          // Below this, even 2 columns squashes each card past its comfortable minimum width -
          // one per line reads far better on a phone than two cramped, wrapping ones.
          { maxWidth: "xs", cols: 1 },
        ]}
      >
        <ModuleCurrentDevice />
        <ModuleCurrentBrowser />
        <ModuleAppleReleases />
        <ModuleGoogleReleases />
      </SimpleGrid>

      {/* What's Next For You and Who's In Today merged into a single full-width module - splitting
          them into two half-width cards left each one fighting for room (3 booking columns
          squeezed into half the page badly wrapped their text), and Who's In Today often had a lot
          of empty space simply because it was height-matched to whatever the other card needed.
          One full-width card lets each section size to its own content and gives the booking rows
          proper breathing room. */}
      <Paper shadow="sm" radius="md" p="md" withBorder>
        <Title order={4} mb="sm">
          What's Next For You
        </Title>

        {isTodayLoading ? (
          <Group spacing="xl" align="flex-start">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} height={54} width={180} radius="md" />
            ))}
          </Group>
        ) : (
          // A wrapping Group rather than a fixed-column grid - each booking gets a guaranteed
          // comfortable minimum width (so its text never gets crushed), and items simply wrap to
          // a new line once they run out of room, growing to fill any extra space on wider
          // screens instead of needlessly wrapping there too.
          <Group spacing="xl" align="flex-start">
            {myNextBookings.map((item) => (
              <Group key={item.key} noWrap spacing="sm" sx={{ flex: "1 1 180px", minWidth: 180 }}>
                <IconBadge color={item.color} icon={item.icon} />
                <Stack spacing={0}>
                  <Text size="xs" color="dimmed">
                    {item.label}
                  </Text>
                  {item.event ? (
                    <>
                      <Text size="sm" fw={600}>
                        {item.when}
                      </Text>
                      {item.detail && (
                        <Text size="xs" color="dimmed">
                          {item.detail}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text size="sm" color="dimmed">
                      {item.emptyText ?? "Nothing booked"}
                    </Text>
                  )}
                </Stack>
              </Group>
            ))}
          </Group>
        )}

        {isWeekday && (
          <>
            <Divider my="md" />

            {/* Who's in today - weekdays only, see isWeekday above */}
            <Group position="apart" mb="sm">
              <Title order={4}>Who's In Today</Title>
              <Badge color="gray" variant="light">
                {moment(today).format("dddd, D MMMM")}
              </Badge>
            </Group>

            {isTodayLoading ? (
              <Group spacing="sm">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height={26} width={80} radius="xl" />
                ))}
              </Group>
            ) : whosInToday.length === 0 ? (
              <Text color="dimmed" size="sm">
                No one's booked a desk or office duty for today yet.
              </Text>
            ) : (
              // Wraps to multiple lines where there's room, but on mobile stays a single,
              // horizontally scrollable line instead of wrapping into a tall block.
              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexWrap: "wrap",
                  gap: theme.spacing.sm,
                  overflowX: "auto",
                  paddingBottom: 4,
                  [theme.fn.smallerThan("sm")]: {
                    flexWrap: "nowrap",
                  },
                })}
              >
                {whosInToday.map((person) => {
                  // Room colours (and the office duty green/red) are plain hex from Edit Company >
                  // Booker, so they're tinted directly rather than via Mantine's named `color` prop -
                  // the accent-colour fallback is a CSS variable, not a real hex value, so it can't be
                  // alpha-suffixed the same way and just falls back to the badge's default styling.
                  const isHex = typeof person.color === "string" && person.color.startsWith("#");
                  return (
                    <Tooltip
                      key={person.name}
                      withArrow
                      label={
                        <Stack spacing={2}>
                          <Text size="sm" fw={700}>
                            {person.name}
                          </Text>
                          {person.details.map((detail) => (
                            <Text key={detail} size="xs">
                              {detail}
                            </Text>
                          ))}
                        </Stack>
                      }
                    >
                      <Badge
                        size="lg"
                        radius="xl"
                        variant="light"
                        styles={{
                          root: {
                            textTransform: "none",
                            flexShrink: 0,
                            ...(isHex
                              ? { backgroundColor: `${person.color}22`, borderColor: `${person.color}55` }
                              : {}),
                          },
                        }}
                        leftSection={
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: person.color,
                            }}
                          />
                        }
                      >
                        {person.name.split(" ")[0]}
                      </Badge>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Posts */}
      <Stack spacing="md">
        <Group position="apart">
          <Title order={4}>Posts</Title>
          {availableTags.length > 0 && (
            <MultiSelect
              placeholder="Filter by tag"
              data={availableTags}
              value={selectedTags}
              onChange={setSelectedTags}
              clearable
              searchable
              w={260}
            />
          )}
        </Group>

        {isPostsLoading ? (
          <SimpleGrid
            cols={3}
            breakpoints={[
              { maxWidth: "md", cols: 2 },
              { maxWidth: "sm", cols: 1 },
            ]}
          >
            <Skeleton height={280} radius="md" />
            <Skeleton height={280} radius="md" />
            <Skeleton height={280} radius="md" />
          </SimpleGrid>
        ) : visiblePosts.length === 0 ? (
          <Paper shadow="sm" radius="md" p="md" withBorder>
            <Center>
              <Title order={3} weight={400} color="dimmed">
                No Posts
              </Title>
            </Center>
          </Paper>
        ) : filteredPosts.length === 0 ? (
          <Paper shadow="sm" radius="md" p="md" withBorder>
            <Center>
              <Title order={3} weight={400} color="dimmed">
                No posts match the selected tags
              </Title>
            </Center>
          </Paper>
        ) : (
          // Mobile shows one post after another in a single column; tablet and desktop show 2 or
          // 3 per row - the same breakpoint pattern already used for the release modules and
          // What's Next/Who's In Today above, so the whole dashboard reflows consistently.
          <SimpleGrid
            cols={3}
            breakpoints={[
              { maxWidth: "md", cols: 2 },
              { maxWidth: "sm", cols: 1 },
            ]}
          >
            {filteredPosts.map((i) => (
              // The full post (body, tags, audience, analytics for admin/super) now lives on its
              // own page at /post/{id} (src/pages/Posts/PostDetail.js) - this card is deliberately
              // just a teaser (image/title/date/author) that navigates there on click, rather than
              // trying to show everything inline here.
              <Card
                key={i.id}
                shadow="sm"
                p="lg"
                radius="md"
                withBorder
                onClick={() => navigate(`/post/${i.id}`)}
                sx={(theme) => ({
                  cursor: "pointer",
                  transition: "box-shadow 100ms ease, transform 100ms ease",
                  "&:hover": {
                    boxShadow: theme.shadows.md,
                    transform: "translateY(-2px)",
                  },
                })}
              >
                {i.data().image && (
                  <Card.Section>
                    {/* imageProps (not a top-level prop) is how Mantine's Image forwards extra
                        attributes to the actual underlying <img> - lazy loading defers
                        downloading a post's image until it's about to scroll into view, instead
                        of every post's image loading immediately even far down the page. */}
                    <Image
                      src={i.data().image}
                      height={160}
                      alt={`${i.data().title} image`}
                      imageProps={{ loading: "lazy" }}
                    />
                  </Card.Section>
                )}

                {/* mt only when there's an image above (spacing it away from the Card.Section) -
                    otherwise the title should sit flush with the card's own top padding, so
                    posts with and without an image line up consistently instead of the
                    image-less ones having an odd gap at the top. */}
                <Stack spacing={2} mt={i.data().image ? "md" : 0}>
                  <Title order={3} color={theme.colorScheme === "dark" ? theme.white : theme.black} lineClamp={1}>
                    {i.data().title}
                  </Title>
                  <Text size="sm" color="dimmed">
                    By {i.data().created_by_name ?? "Unknown"} ·{" "}
                    {/* publish_date (set on create and refreshed every time a post is
                        (re)published - see addPost/updatePost/publishNow in services/Firestore.js
                        and Posts.js) is when this actually went live, which is what a reader on
                        the Dashboard cares about - not `created`, the original draft/creation
                        timestamp, which is what this used to show here by mistake. Falls back to
                        `created` only for a legacy post saved before publish_date existed.
                        Formatted in the viewer's own local time (moment, not .toUTCString()) - the
                        raw UTC instant used to show here instead, which looked like a post
                        scheduled for e.g. 09:00 had actually gone out an hour early during BST. */}
                    {moment((i.data().publish_date ?? i.data().created).toDate()).format("D MMM YYYY, HH:mm")}
                  </Text>

                  {/* Analytics - admin/super only (also enforced server-side by firestore.rules;
                      this check just avoids ever firing a request a non-admin's read would be
                      rejected anyway). Shows a lightweight summary here - the full per-viewer
                      breakdown lives on the post's own detail page. */}
                  {isAdminOrSuper && (
                    <Group spacing={10} mt={2}>
                      <Group spacing={4}>
                        <IconEye size="0.9rem" stroke={1.75} color={theme.colors.gray[6]} />
                        <Text size="xs" color="dimmed">
                          {postViewsById[i.id] === undefined
                            ? "…"
                            : `${postViewsById[i.id].reduce((sum, v) => sum + (v.view_count ?? 1), 0)} view${
                                postViewsById[i.id].reduce((sum, v) => sum + (v.view_count ?? 1), 0) === 1 ? "" : "s"
                              }`}
                        </Text>
                      </Group>
                      <Group spacing={4}>
                        <IconUsers size="0.9rem" stroke={1.75} color={theme.colors.gray[6]} />
                        <Text size="xs" color="dimmed">
                          {postViewsById[i.id] === undefined
                            ? "…"
                            : `${postViewsById[i.id].length} viewer${postViewsById[i.id].length === 1 ? "" : "s"}`}
                        </Text>
                      </Group>
                    </Group>
                  )}
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Stack>
  );
}

export default Dashboard;
